import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Tab, Label, Menu } from 'semantic-ui-react';
import { FarmersService } from '~/api/services/farmers/index';
import { RouteTypes } from '~/routes/constants';
import { FarmerProfileLogo, StyledDefaultProfile } from './style'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useAuth } from '~/routes/components/AuthProvider';
import { CLIMATE_CHECK_SLUG, CO2_LFL_SLUG, FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_MONEY_ACCOUNT_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import { handleErrors } from '~/utils/helpers';
import { useHash } from '~/components/useHash';

//Tabs
import Users from './Tabs/Users';
import ArticleBookings from './Tabs/ArticleBookings';
import TradingActivities from './Tabs/TradingActivities';
import FinancialInformation from './Tabs/FinancialInformation';
import FarmerDetails from './Tabs/FarmerDetails';
import BookingHistory from './Tabs/BookingHistory';
import TradingAmounts from './Tabs/TradingAmounts';
import DairyData from './Tabs/DairyData';
import Contacts from './Tabs/Contacts';
import Notes from './Tabs/Notes';
import MaterialInfo from './Tabs/MaterialInfo';
import MgaDetails from './Tabs/MGADetails';
import MilkSamples from './Tabs/MilkSamples'
import MilkDeliveries from './Tabs/MilkDeliveries';
import CO2Footprint from './Tabs/CO2Footprint';
import HedgingInfo from './Tabs/HedgingInfo';
import Protocols from './Tabs/Protocols';

const ShowFarmer: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { modules } } = useAuth()
  const milkMoneyModuleEnabled = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)
  const co2ModuleEnabled = modules.find(module => module.slug === CO2_LFL_SLUG)
  const climaCheckModuleEnabled = modules.find(module => module.slug === CLIMATE_CHECK_SLUG)
  const tradingActivitieEnabled = modules.find(module => module.slug === MILK_FIXED_SLUG || module.slug === FAT_PROTEIN_SLUG || module.slug === MILK_STOCK_EXCHANGE)

  const { id } = useParams()
  const [hash, setHash] = useHash();

  const [isBusy, setIsBusy] = useState(false)
  const [showData, setShowData] = useState(null)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)

  const { data: farmerProfileSrc } = FarmersQuery.getFarmerProfileFile(id)
  const {
    data: farmerOptions,
    isLoading: isLoadingOptions,
    refetch: refetchOptions
  } = FarmersQuery.getFarmerOptions()

  const panes = [
    {
      menuItem: t('Farmer details'),
      key: 'profile-details',
      to: '',
      render: () => <FarmerDetails farmerOptions={farmerOptions}/>
    },
    {
      menuItem: t('Farmer User'),
      key: 'users',
      render: () => <Users />
    },
    {
      menuItem: t('Contacts'),
      key: 'contacts',
      render: () => <Contacts/>
    },
    {
      menuItem: t('Communication'),
      key: 'communication',
      render: () => <Notes />
    },
    {
      menuItem: t('Material infor.'),
      key: 'material-info',
      render: () => <MaterialInfo
        createdAt={showData?.farmer?.createdAt}
        farmer={showData?.farmer}
      />
    },
    {
      menuItem: t('Financial information'),
      key: 'financial-information',
      render: () => <FinancialInformation
        createdAt={showData?.farmer?.createdAt}
        farmerData={showData?.farmer}
      />
    },
    {
      menuItem: t('MGA details'),
      key: 'mga-details',
      render: () => <MgaDetails/>,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Milk samples'),
      key: 'milk-samples',
      render: () => <MilkSamples/>,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Milk Deliveries'),
      key: 'milk-deliveries',
      render: () => <MilkDeliveries/>,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Article bookings'),
      key: 'article-bookings',
      render: () => <ArticleBookings
        farmer={`${showData?.farmer?.companyName} (${showData?.farmer?.supplierNo || ''})`}
        farmerData={showData?.farmer}
      />,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: t('Booking history'),
      key: 'bookings-history',
      render: () => <BookingHistory />,
      remove: !milkMoneyModuleEnabled
    },
    {
      menuItem: <Menu.Item key={'co2-data'}><div dangerouslySetInnerHTML={{ __html: t('CO2 data') }}/></Menu.Item>,
      key: 'co2-data',
      render: () => <DairyData
        co2ModuleEnabled={Boolean(co2ModuleEnabled)}
        climaCheckModuleEnabled={Boolean(climaCheckModuleEnabled)}
      />,
      remove: !(co2ModuleEnabled || climaCheckModuleEnabled)
    },
    {
      menuItem: <Menu.Item key={'co2-footprint'}><div dangerouslySetInnerHTML={{ __html: t('CO2 footprint') }}/></Menu.Item>,
      key: 'co2-footprint',
      render: () => <CO2Footprint />,
      remove: !co2ModuleEnabled
    },
    {
      menuItem: t('Hedging info.'),
      key: 'hedging-info',
      render: () => <HedgingInfo farmerOptions={farmerOptions}/>,
      remove: !tradingActivitieEnabled
    },
    {
      menuItem: t('Trading amounts'),
      key: 'trading-amounts',
      render: () => <TradingAmounts />,
      remove: !tradingActivitieEnabled
    },
    {
      menuItem: t('Trading activities'),
      key: 'trading-activities',
      render: () => <TradingActivities
        statusOptions={showData?.tradingAmountStatuses}
      />,
      remove: !tradingActivitieEnabled
    },
    {
      menuItem: t('Protocols'),
      key: 'protocols',
      render: () => <Protocols/>
    }
  ]

  useEffect(() => {
    fetchShowData()
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (hash !== '') {
      const index = panes.filter(pane => !pane.remove)?.findIndex((option) => option.key === hash.toString().split('#')[1])
      setActiveIndex(index !== -1 ? index : 0)
    }
  }, [hash])

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const showDataResponse = await FarmersService.getShowFarmerData(id)
      setShowData(showDataResponse.data)
      if (hash !== '') {
        const index = panes.filter(pane => !pane.remove)?.findIndex((option) => option.key === hash.toString().split('#')[1])
        setActiveIndex(index !== -1 ? index : 0)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderPanes() {
    return <Tab
      style={{ margin: '30px 0px' }}
      menu={{ secondary: true, pointing: true }}
      onTabChange={(e, data) => {
        const panesFiltered = panes.filter(pane => !pane.remove)
        setHash(panesFiltered[data.activeIndex].key)
      }}
      activeIndex={activeIndex}
      panes={panes.filter(pane => !pane.remove)}
    />
  }

  function renderDefaultImage() {
    return <StyledDefaultProfile color='blue' name='user circle' size='big' />
  }

  function renderImage() {
    return <LazyLoadImage
      alt={''}
      effect='blur'
      src={farmerProfileSrc} />
  }

  function renderFarmerDetails() {
    const supplierNo = showData?.farmer?.supplierNo
    return !isBusy && <FarmerProfileLogo>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div className='farmer-logo-container'>
          {farmerProfileSrc ? renderImage() : renderDefaultImage()}
        </div>
        <div className='farmer-name-container'>
          {supplierNo && <div className='farmer-supplier'>
            {t('Supplier No. {{supplierNo}}', { supplierNo })}
            {showData?.farmer?.abbreviation && <Label className='farmer-abbrevation'>
              {showData?.farmer?.abbreviation}
            </Label>}
          </div>}
          <div className='farmer-company-name'>{showData?.farmer?.companyName}</div>
        </div>
      </div>
    </FarmerProfileLogo>
  }

  return <Layout
    isBusy={isBusy || isLoadingOptions}
    segmented
    title={t('Farmer profile')}
    close={RouteTypes.DAIRY_FACTORY_FARMERS}
  >
    {renderFarmerDetails()}
    {renderPanes()}
  </Layout>;
};

export default ShowFarmer;
